import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from './button';
import { 
  MessageCircle, X, Send, Bot, User, ArrowRight,
  Briefcase, GraduationCap, Users, Phone, MapPin, BookOpen,
  Building, Sparkles, ExternalLink
} from 'lucide-react';
import { cn } from '../../lib/utils';

const API = '/api';
const WHATSAPP_NUMBER = '4915906453419';

// Quick action buttons configuration
const getQuickActions = (language) => [
  { 
    id: 'training', 
    label: language === 'ar' ? 'استكشف الدورات التدريبية' : 'Explore Training Courses',
    icon: GraduationCap,
    link: '/courses'
  },
  { 
    id: 'recruitment', 
    label: language === 'ar' ? 'طلب دعم التوظيف' : 'Request Recruitment Support',
    icon: Briefcase,
    link: '/recruitment-request'
  },
  { 
    id: 'jobs', 
    label: language === 'ar' ? 'تقدم لوظيفة' : 'Apply for a Job',
    icon: Building,
    link: '/jobs'
  },
  { 
    id: 'talent', 
    label: language === 'ar' ? 'انضم إلى مجموعة المواهب' : 'Join the Talent Pool',
    icon: Users,
    link: '/talent-pool'
  },
  { 
    id: 'custom-training', 
    label: language === 'ar' ? 'طلب تدريب مخصص' : 'Request Customized Training',
    icon: Sparkles,
    link: '/customized-training-request'
  },
  { 
    id: 'contact', 
    label: language === 'ar' ? 'تواصل معنا' : 'Contact Us',
    icon: Phone,
    link: '/contact'
  },
];

// Suggested questions
const getSuggestedQuestions = (language) => [
  language === 'ar' ? 'ما هي الخدمات التي تقدمونها؟' : 'What services do you offer?',
  language === 'ar' ? 'في أي مدن تقدمون الدورات التدريبية؟' : 'In which cities do you offer training?',
  language === 'ar' ? 'ما هي مواضيع التدريب المتاحة؟' : 'What training topics do you cover?',
  language === 'ar' ? 'هل تقدمون تدريبًا باللغة العربية؟' : 'Do you offer training in Arabic?',
];

export const ChatBot = () => {
  const { language, direction } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Initialize with welcome message when opened
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessage = {
        id: Date.now(),
        type: 'bot',
        content: language === 'ar' 
          ? 'مرحباً! أنا مساعد WeDo الافتراضي. كيف يمكنني مساعدتك اليوم؟'
          : "Hello! I'm WeDo's virtual assistant. How can I help you today?",
        timestamp: new Date(),
      };
      setMessages([welcomeMessage]);
    }
  }, [isOpen, language]);

  const handleSendMessage = async (messageText = inputValue) => {
    if (!messageText.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: messageText,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setShowQuickActions(false);
    setIsLoading(true);

    try {
      const response = await fetch(`${API}/chatbot/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          message: messageText,
          language: language,
          conversation_history: messages.slice(-6).map(m => ({
            role: m.type === 'user' ? 'user' : 'assistant',
            content: m.content
          }))
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const botMessage = {
          id: Date.now() + 1,
          type: 'bot',
          content: data.response,
          actions: data.actions || [],
          showWhatsApp: data.show_whatsapp || false,
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, botMessage]);
      } else {
        throw new Error('Failed to get response');
      }
    } catch (error) {
      console.error('Chatbot error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: language === 'ar'
          ? 'عذراً، حدث خطأ. يمكنك التواصل معنا مباشرة عبر واتساب.'
          : 'Sorry, something went wrong. You can reach us directly on WhatsApp.',
        showWhatsApp: true,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickAction = (action) => {
    if (action.link) {
      window.location.href = action.link;
    }
  };

  const handleSuggestedQuestion = (question) => {
    handleSendMessage(question);
  };

  const quickActions = getQuickActions(language);
  const suggestedQuestions = getSuggestedQuestions(language);

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "fixed z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300",
          "bg-[#33AADD] hover:bg-[#2899CC] text-white",
          "hover:scale-110 active:scale-95",
          direction === 'rtl' ? 'left-6' : 'right-6',
          "bottom-20" // Moved higher to avoid Emergent badge
        )}
        data-testid="chatbot-toggle"
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
      >
        {isOpen ? (
          <X className="w-6 h-6" />
        ) : (
          <MessageCircle className="w-6 h-6" />
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div
          className={cn(
            "fixed z-50 bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden",
            "w-[calc(100%-2rem)] sm:w-96 h-[500px] max-h-[80vh]",
            direction === 'rtl' ? 'left-4 sm:left-6' : 'right-4 sm:right-6',
            "bottom-36" // Adjusted for new button position
          )}
          style={{ animation: 'slideUp 0.3s ease-out' }}
          data-testid="chatbot-window"
        >
          {/* Header */}
          <div className="bg-[#33AADD] text-white p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Bot className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold">
                {language === 'ar' ? 'مساعد WeDo' : 'WeDo Assistant'}
              </h3>
              <p className="text-xs text-white/80">
                {language === 'ar' ? 'متصل الآن' : 'Online now'}
              </p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="w-8 h-8 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#F9FAFB]">
            {messages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  "flex gap-2",
                  message.type === 'user' ? 'justify-end' : 'justify-start'
                )}
              >
                {message.type === 'bot' && (
                  <div className="w-8 h-8 rounded-full bg-[#33AADD]/10 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-[#33AADD]" />
                  </div>
                )}
                <div
                  className={cn(
                    "max-w-[80%] rounded-2xl px-4 py-2.5",
                    message.type === 'user'
                      ? 'bg-[#33AADD] text-white rounded-br-sm'
                      : 'bg-white text-[#111827] shadow-sm rounded-bl-sm'
                  )}
                >
                  <p className="text-sm whitespace-pre-line">{message.content}</p>
                  
                  {/* Action Buttons */}
                  {message.actions && message.actions.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {message.actions.map((action, idx) => (
                        <a
                          key={idx}
                          href={action.link}
                          className="inline-flex items-center gap-1 text-xs bg-[#33AADD]/10 text-[#33AADD] px-3 py-1.5 rounded-full hover:bg-[#33AADD]/20 transition-colors"
                        >
                          {action.label}
                          <ArrowRight className="w-3 h-3" />
                        </a>
                      ))}
                    </div>
                  )}

                  {/* WhatsApp Fallback */}
                  {message.showWhatsApp && (
                    <a
                      href={`https://wa.me/${WHATSAPP_NUMBER}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 flex items-center gap-2 bg-[#25D366] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#20BD5A] transition-colors"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      {language === 'ar' ? 'تواصل عبر واتساب' : 'Chat on WhatsApp'}
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
                {message.type === 'user' && (
                  <div className="w-8 h-8 rounded-full bg-[#33AADD] flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
            ))}

            {/* Loading indicator */}
            {isLoading && (
              <div className="flex gap-2 justify-start">
                <div className="w-8 h-8 rounded-full bg-[#33AADD]/10 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-4 h-4 text-[#33AADD]" />
                </div>
                <div className="bg-white rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-[#33AADD] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-[#33AADD] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-[#33AADD] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            {showQuickActions && messages.length <= 1 && (
              <div className="space-y-3">
                <p className="text-xs text-[#6B7280] font-medium">
                  {language === 'ar' ? 'إجراءات سريعة:' : 'Quick actions:'}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {quickActions.slice(0, 6).map((action) => (
                    <button
                      key={action.id}
                      onClick={() => handleQuickAction(action)}
                      className="flex items-center gap-2 p-2.5 bg-white rounded-xl text-left text-xs font-medium text-[#111827] hover:bg-[#33AADD]/5 hover:text-[#33AADD] transition-colors shadow-sm border border-[#E5E7EB]"
                    >
                      <action.icon className="w-4 h-4 flex-shrink-0" />
                      <span className="line-clamp-2">{action.label}</span>
                    </button>
                  ))}
                </div>

                <p className="text-xs text-[#6B7280] font-medium mt-4">
                  {language === 'ar' ? 'أو اسأل سؤالاً:' : 'Or ask a question:'}
                </p>
                <div className="flex flex-wrap gap-2">
                  {suggestedQuestions.map((question, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSuggestedQuestion(question)}
                      className="text-xs px-3 py-1.5 bg-white rounded-full text-[#6B7280] hover:bg-[#33AADD]/10 hover:text-[#33AADD] transition-colors border border-[#E5E7EB]"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 bg-white border-t border-[#E5E7EB]">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSendMessage();
              }}
              className="flex gap-2"
            >
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={language === 'ar' ? 'اكتب رسالتك...' : 'Type your message...'}
                className="flex-1 px-4 py-2.5 rounded-full border border-[#E5E7EB] focus:outline-none focus:border-[#33AADD] focus:ring-1 focus:ring-[#33AADD] text-sm"
                disabled={isLoading}
                data-testid="chatbot-input"
              />
              <button
                type="submit"
                disabled={!inputValue.trim() || isLoading}
                className="w-10 h-10 rounded-full bg-[#33AADD] text-white flex items-center justify-center hover:bg-[#2899CC] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="chatbot-send"
              >
                <Send className="w-5 h-5" />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBot;
